// Adresses et langues : fonctions PURES (aucun accès au navigateur), utilisables partout.
//
// Structure des adresses : le français n'a PAS de préfixe (aucune adresse existante ne change),
// les autres langues ont le leur : /en/faire-part-mariage-anime, /de/…, /es/…
// Les composants n'ont presque jamais besoin de ces fonctions : le routeur (I18nRouter.js) ajoute
// tout seul le préfixe aux <Link> et aux navigate(). On s'en sert pour les adresses ABSOLUES
// (canonical, hreflang, liens d'e-mail ou de partage) et pour les <a href> bruts.
import { DEFAULT_LANG, LANG_CODES, LANGUAGES, SITE_URL, RECIPIENT_PREFIXES, ADMIN_PREFIXES } from './config';

const PREFIXED = LANG_CODES.filter((c) => c !== DEFAULT_LANG);

// Sépare « /chemin?requête#ancre » en ses trois morceaux.
const split = (url) => {
  const m = /^([^?#]*)(.*)$/.exec(url || '');
  return { path: m[1], rest: m[2] };
};

const isExternal = (url) => /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(url || '');

/** Langue d'un chemin préfixé (« /en/panier » → 'en'), sinon null. Seules les langues actives comptent. */
export const langFromPath = (pathname) => {
  const m = /^\/([a-z]{2,3})(?=\/|$)/.exec(pathname || '');
  return m && PREFIXED.includes(m[1]) ? m[1] : null;
};

/** Chemin sans préfixe de langue (« /en/panier » → '/panier', « /en » → '/'). */
export const stripLang = (pathname) => {
  const lang = langFromPath(pathname);
  if (!lang) return pathname || '/';
  return (pathname || '').slice(lang.length + 1) || '/';
};

/** Cette adresse est-elle celle d'une page destinataire (carte, faire-part) ? Langue = celle du lecteur. */
export const isRecipientPath = (pathname) => {
  const p = stripLang(pathname);
  return RECIPIENT_PREFIXES.some((prefix) => p === prefix.replace(/\/$/, '') || p.startsWith(prefix));
};

/** Écran d'administration : jamais traduit, jamais dans un sitemap. */
export const isAdminPath = (pathname) => {
  const p = stripLang(pathname);
  return ADMIN_PREFIXES.some((prefix) => p.startsWith(prefix));
};

/** Cette page existe-t-elle « par langue » (donc avec hreflang et canonical par langue) ? */
export const isLocalizablePath = (pathname) => !isRecipientPath(pathname) && !isAdminPath(pathname);

/**
 * Adresse d'une page dans une langue.  localizedPath('/faire-part-mariage', 'en') → '/en/faire-part-mariage'
 * Le français n'a pas de préfixe ; la requête (?theme=…) et l'ancre (#…) sont conservées ; une adresse
 * externe (https://…, mailto:…) ou une simple ancre est rendue telle quelle.
 */
export const localizedPath = (url, lang = DEFAULT_LANG) => {
  if (!url || isExternal(url) || url.startsWith('#') || url.startsWith('?')) return url;
  const { path, rest } = split(url);
  const bare = stripLang(path.startsWith('/') ? path : `/${path}`);
  if (lang === DEFAULT_LANG || !PREFIXED.includes(lang)) return bare + rest;
  return `/${lang}${bare === '/' ? '/' : bare}${rest}`;
};

/** Adresse absolue (https://joli-mot.fr/en/…) — pour canonical, hreflang, sitemaps, e-mails. */
export const absoluteUrl = (url, lang = DEFAULT_LANG) => {
  const local = localizedPath(url, lang);
  return isExternal(local) ? local : `${SITE_URL}${local}`;
};

/** Liens « alternate » d'une page : une entrée par langue active + x-default (= le français). */
export const alternateLinks = (path) => {
  const links = LANGUAGES.map((l) => ({ hreflang: l.code, href: absoluteUrl(path, l.code) }));
  links.push({ hreflang: 'x-default', href: absoluteUrl(path, DEFAULT_LANG) });
  return links;
};
